import _ from "lodash";
import moment from "moment-timezone";
import { getGlobalTimezone } from "./TimezoneHelpers";
import { Sentry } from "services/sentry";

export const isTouchable = () => {
  const userAgent = getUserAgent();

  return (
    !!userAgent && userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i)
  );
};

export const isTouchableV2 = () => {
  if (_.get(window, "matchMedia")) {
    return _.get(window.matchMedia("(pointer: coarse)"), "matches", false);
  }

  return false;
};

export const isTouchableV3 = (maxScreenWidth = 768) => {
  if (!window.matchMedia) {
    return false;
  }

  const isTouchscreen = window.matchMedia("(pointer: coarse)").matches;

  const isMobileDevice =
    Math.min(window.screen.width, window.screen.height) <= maxScreenWidth ||
    navigator.userAgent.includes("Mobi");

  return isTouchscreen && isMobileDevice;
};

export const getUserAgent = () => {
  return (
    _.get(window, "navigator.userAgent", undefined) ||
    _.get(navigator, "userAgent") ||
    _.get(navigator, "vendor") ||
    _.get(window, "opera")
  );
};
export const isIphone = () => {
  const userAgent = getUserAgent();

  return !!userAgent && userAgent.match(/(iPad)|(iPhone)|(iPod)/i);
};

export const isAndroid = () => {
  const userAgent = getUserAgent();

  return !!userAgent && userAgent.match(/(android)|(webOS)/i);
};

export const isMobile = () => {
  // const userAgent = _.get(window, "navigator.userAgent", undefined);
  // return !!userAgent && userAgent.match(/(iPhone)|(iPod)|(android)/i);
  return isTouchable() ? window.outerWidth <= 776 : window.innerWidth <= 776;
};

export const isIpad = () => {
  return (
    (/iPad/.test(navigator.platform) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) && // ipad 12 +
    !window.MSStream
  );
};

export const isTablet = () => {
  return isTouchable()
    ? window.outerWidth <= 1024 && window.outerWidth > 776
    : window.innerWidth <= 1024 && window.innerWidth > 776;
};

export const isDesktop = () => {
  return isTouchable() ? window.outerWidth > 1024 : window.innerWidth > 1024;
};

export const isSafari = () => {
  const userAgent = _.toLower(_.get(window, "navigator.userAgent", ""));
  return userAgent.search("safari") > -1 && userAgent.search("chrome") < 0;
};

export const isChrome = () => {
  const isChromium = window.chrome;
  const winNav = window.navigator;
  const vendorName = winNav.vendor;
  const isOpera = typeof window.opr !== "undefined";
  const isIEedge = winNav.userAgent.indexOf("Edg") > -1;

  return (
    isChromium !== null &&
    typeof isChromium !== "undefined" &&
    vendorName === "Google Inc." &&
    isOpera === false &&
    isIEedge === false
  );
};

export const isSmallMobile = () => {
  return (
    isTouchable() && (window.outerHeight <= 450 || window.outerWidth <= 800)
  );
};

const getCountryCode = () => {
  try {
    const localeTimeZone = getGlobalTimezone();
    const localeCountryCodes = moment.tz.zone(localeTimeZone).countries();
    return _.first(localeCountryCodes);
  } catch {
    return "";
  }
};

const checkChinaAndroid = () => {
  try {
    const isAndroidDevice = isAndroid();
    const countryCode = getCountryCode();
    return isAndroidDevice && countryCode == "CN";
  } catch {
    return false;
  }
};

export const isWindows = () => {
  return _.includes(getUserAgent(), "Windows");
};

export const isMac = () => {
  const userAgent = getUserAgent();
  return (
    _.includes(userAgent, "Macintosh") || _.includes(userAgent, "Mac OS X")
  );
};

const storeLinks = {
  STUDENT: {
    ios: `itms-apps://itunes.apple.com/app/toddle-student/id1473393667?ls=1&mt=8`,
    android: "http://play.google.com/store/apps/details?id=com.toddle.student",
  },
  STAFF: {
    ios: `itms-apps://apps.apple.com/app/id1529065681`,
    android: "http://play.google.com/store/apps/details?id=com.toddle.teacher",
  },
  FAMILY_MEMBER: {
    ios: `https://apps.apple.com/app/toddle-family/id1473392321`,
    android: "http://play.google.com/store/apps/details?id=com.toddle.family",
  },
};

const s3ApkLinks = {
  STUDENT: "http://cloud.toddleapp.com/app_builds/Student/student_latest.apk",
  STAFF: "http://cloud.toddleapp.com/app_builds/Educator/educator_latest.apk",
  FAMILY_MEMBER:
    "http://cloud.toddleapp.com/app_builds/Family/family_latest.apk",
};

const familyAppLinks = {
  china: {
    android: "https://toddlefamilychinadownload.toddleapp.cn/SNKY/h3777ex1",
    ios: "https://apps.apple.com/cn/app/toddle-family-china/id6474382315",
  },
  global: {
    android: "https://play.google.com/store/apps/details?id=com.toddle.family",
    ios: "https://apps.apple.com/app/toddle-family/id1473392321",
  },
};

export const goToAppStore = ({
  appName = "FAMILY_MEMBER",
  useApkDownloadLink = false,
} = {}) => {
  const isAndroidDevice = isAndroid();

  const isChinaAndroid =
    checkChinaAndroid() || (useApkDownloadLink && isAndroidDevice);

  if (!isChinaAndroid) {
    const platformOS = isAndroidDevice ? "android" : "ios";
    window.location.href = storeLinks[appName][platformOS];
  } else {
    window.location.href = s3ApkLinks[appName];
  }
};

export const downloadButtonRedirectionForFamilyApp = ({
  isChinaEnv = false,
}) => {
  const isAndroidDevice = isAndroid();

  const env = isChinaEnv ? "china" : "global";
  const platform = isAndroidDevice ? "android" : "ios";

  window.location.href = familyAppLinks[env][platform];
};

export const isSafeExamBrowser = () => {
  return getUserAgent().toLowerCase().includes("seb");
};

export const isChromebook = () => {
  return /\bCrOS\b/.test(getUserAgent());
};

export const isInKioskMode = () => {
  try {
    const isChromebookDevice = isChromebook();
    if (!isChromebookDevice) return false; // * Kiosk mode is only applicable for Chromebooks, so taking early exit
    const search = window?.location?.search;
    const urlAppType = new URLSearchParams(search).get("appType");
    const sessionAppType = sessionStorage?.getItem("appType");
    return _.includes([urlAppType, sessionAppType], "cros");
  } catch (error) {
    Sentry.captureMessage(`KioskModeDetectionError: ${error.message}`);
    return false; // * Returning false for any error
  }
};

export const isMobileOrTablet = () => {
  const userAgent = getUserAgent();
  const AGENTS = /android|iphone|ipad|ipod|blackberry|bb|playbook|windows phone|kindle|silk|opera mini|mobile|tablet/;
  // isIpad() is added as a fallback to detect iPadOS devices that may not be caught by the regex
  // iPadOS 13+ can present itself as desktop Safari, so the user agent may not contain 'ipad'
  return AGENTS.test(userAgent?.toLowerCase()) || isIpad();
};

// UA-based detection for mobile phones (not tablets)
export const isMobileByUserAgent = () => {
  try {
    const ua = (getUserAgent() || "").toLowerCase();
    // Treat classic phone UAs as mobile; exclude tablets (android tablets often lack 'mobile')
    return (
      /iphone|ipod|windows phone|blackberry|bb|opera mini/.test(ua) ||
      (/android/.test(ua) && /mobile/.test(ua))
    );
  } catch {
    return false;
  }
};

// UA-based detection for tablets (iPad, Android tablets, Kindle, Playbook, Silk)
export const isTabletByUserAgent = () => {
  try {
    const ua = (getUserAgent() || "").toLowerCase();
    // iPad/Kindle/Playbook/Silk or Android tablets (android without 'mobile') or iPadOS fallback
    return (
      /ipad|playbook|silk|kindle|tablet/.test(ua) ||
      (/android/.test(ua) && !/mobile/.test(ua)) ||
      isIpad()
    );
  } catch {
    return false;
  }
};
